/**
 * DeployAsAgentButton — Desk+ “deploy library template as agent” entry.
 *
 * WORK-PLAN C3: library UX under agent model (“deploy as agent/strategy”),
 * not copy-trader feed. Multi-exchange grid/DCA libraries start on Advanced.
 *
 * Advanced / Professional / Enterprise (`canUseGridDca`):
 *   - mode="navigate" (default): go to /ai-trading?template=<id>…
 *   - mode="panel": honest configure-agent panel stub (no fake live deploy)
 *
 * Free / Starter: cannot complete deploy — upgrade path to Advanced.
 */

'use client'

import React, { useCallback, useId, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { usePlanEntitlements } from '@/hooks/use-plan-entitlements'
import styles from './DeployAsAgentButton.module.css'

export type LibraryTemplateKind = 'grid' | 'dca'

export type DeployAsAgentMode = 'navigate' | 'panel'

export interface DeployAsAgentButtonProps {
  /** Library template id passed to ai-trading as `template` query param. */
  templateId: string
  /** Optional display name for the stub panel. */
  templateName?: string
  /** Grid or DCA library kind (query + copy). */
  kind?: LibraryTemplateKind
  /**
   * Desk+ click behaviour:
   * - navigate — push /ai-trading?template=…
   * - panel — open honest configure-agent stub (no network deploy)
   */
  mode?: DeployAsAgentMode
  /** Optional class on outer wrap. */
  className?: string
  /** Button label override (default “Deploy as agent”). */
  label?: string
  /** Compact density for card footers. */
  compact?: boolean
  /**
   * Called when Desk+ navigation is about to run (not on Free/Starter block).
   * Does not imply the agent was created — only that the operator left for
   * the agent desk with a template query.
   */
  onNavigate?: (href: string) => void
}

function buildAiTradingHref(
  templateId: string,
  kind: LibraryTemplateKind
): string {
  const params = new URLSearchParams()
  params.set('template', templateId)
  params.set('kind', kind)
  params.set('intent', 'deploy')
  return `/ai-trading?${params.toString()}`
}

const KIND_LABEL: Record<LibraryTemplateKind, string> = {
  grid: 'Grid',
  dca: 'DCA',
}

/**
 * Desk+ deploy control for multi-exchange grid/DCA library templates.
 * Starter and Free never complete a deploy from this control.
 */
export function DeployAsAgentButton({
  templateId,
  templateName,
  kind = 'grid',
  mode = 'navigate',
  className = '',
  label = 'Deploy as agent',
  compact = false,
  onNavigate,
}: DeployAsAgentButtonProps) {
  const router = useRouter()
  const panelTitleId = useId()
  const { canUseGridDca, isLoading, tier, isFree } = usePlanEntitlements()
  const [panelOpen, setPanelOpen] = useState(false)
  const [blockedNote, setBlockedNote] = useState<string | null>(null)

  const href = buildAiTradingHref(templateId, kind)
  const displayName = templateName?.trim() || `${KIND_LABEL[kind]} template`
  const entitled = canUseGridDca

  const goToAgentDesk = useCallback(() => {
    onNavigate?.(href)
    router.push(href)
  }, [href, onNavigate, router])

  const handlePrimaryClick = useCallback(() => {
    setBlockedNote(null)

    if (isLoading) return

    if (!entitled) {
      setBlockedNote(
        isFree || tier === 'none'
          ? 'Deploying library templates as agents requires Desk ($99.99/mo). Free is desk views only — no agents, no library deploy.'
          : 'Grid and DCA libraries are on Desk ($99.99/mo). Operator covers core agents and manual execution but cannot deploy library templates.'
      )
      setPanelOpen(false)
      return
    }

    if (mode === 'panel') {
      setPanelOpen((open) => !open)
      return
    }

    goToAgentDesk()
  }, [entitled, goToAgentDesk, isFree, isLoading, mode, tier])

  const handleContinueFromPanel = useCallback(() => {
    if (!entitled) {
      setBlockedNote(
        'Library deploy requires Advanced. This stub does not create an agent on Free or Starter.'
      )
      return
    }
    setPanelOpen(false)
    goToAgentDesk()
  }, [entitled, goToAgentDesk])

  const busy = isLoading
  const primaryDisabled = busy

  return (
    <div
      className={[styles.wrap, compact ? styles.compact : '', className]
        .filter(Boolean)
        .join(' ')}
      data-deploy-as-agent={templateId}
      data-deploy-entitled={entitled ? 'true' : 'false'}
      data-deploy-kind={kind}
    >
      <div className={styles.actions} role="group" aria-label={`Deploy ${displayName} as agent`}>
        <button
          type="button"
          className={`${styles.btn} ${styles.btnPrimary}`}
          onClick={handlePrimaryClick}
          disabled={primaryDisabled}
          aria-busy={busy || undefined}
          aria-expanded={mode === 'panel' ? panelOpen : undefined}
          aria-controls={mode === 'panel' && panelOpen ? panelTitleId : undefined}
          title={
            entitled
              ? mode === 'panel'
                ? 'Open configure-agent stub, then continue to agent desk'
                : 'Open agent desk with this library template'
              : 'Requires Desk plan — grid/DCA library deploy'
          }
        >
          {busy ? 'Checking plan…' : label}
        </button>
      </div>

      {!entitled && !isLoading && blockedNote ? (
        <div className={styles.gateStrip} role="status">
          <p className={styles.gateText}>{blockedNote}</p>
          <div className={styles.gateActions}>
            <Link href="/settings/billing" className={`${styles.btn} ${styles.btnPrimary} ${styles.btnSm}`}>
              Upgrade to Desk
            </Link>
            <Link href="/settings/billing" className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}>
              Start free
            </Link>
          </div>
        </div>
      ) : null}

      {entitled && mode === 'panel' && panelOpen ? (
        <section
          className={styles.panel}
          aria-labelledby={panelTitleId}
          data-deploy-panel="configure-stub"
        >
          <header className={styles.panelBezel}>
            <div className={styles.panelBezelLeft}>
              <span className={styles.panelMark} aria-hidden />
              <span className={styles.panelBezelLabel}>Agent · configure</span>
            </div>
            <span className={styles.panelBezelStatus}>Stub</span>
          </header>

          <div className={styles.panelBody}>
            <p className={styles.panelEyebrow}>
              {KIND_LABEL[kind]} library · deploy as agent
            </p>
            <h3 id={panelTitleId} className={styles.panelTitle}>
              Configure agent
            </h3>
            <p className={styles.panelText}>
              Template <span className={styles.mono}>{displayName}</span>
              {templateId ? (
                <>
                  {' '}
                  (<span className={styles.mono}>{templateId}</span>)
                </>
              ) : null}{' '}
              will open on the agent desk. This control does not invent a live
              deploy or place orders — you finish setup and supervision under{' '}
              <span className={styles.mono}>/ai-trading</span>.
            </p>
            <p className={styles.panelNote}>
              Your agents after deploy — not a copy-trading or signal marketplace.
            </p>

            <div className={styles.panelActions}>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnPrimary}`}
                onClick={handleContinueFromPanel}
              >
                Continue to agent desk
              </button>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnSecondary}`}
                onClick={() => setPanelOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </section>
      ) : null}
    </div>
  )
}

export default DeployAsAgentButton
